import Clickable from '/components/Clickable';
import CutStepTitle from './cut-step/CutStepTitle';
import Image from '/components/Image';
import PropTypes from 'prop-types';
import SizeStep from '/components/products/pack-builder/one-flow/SizeStep';
import Typography from '/components/Typography';

import classNames from '/utils/class-names';
import { useWizard } from 'react-use-wizard';

import { useEffect, useState } from 'react';

export default function CutStep({
	builderConfig,
	packConfig,
	setActiveProductType,
	updatePackConfig,
	updatePackProductConfig,
}) {
	const foundProductConfig = packConfig.productConfigs[packConfig.currentProductConfigIndex];
	const foundSelectedStyle = builderConfig?.styles?.find(
		(style) => style?.productType === foundProductConfig?.productType?.productType,
	);

	const [productConfig, setProductConfig] = useState(foundProductConfig || {});
	const [selectedStyle, setSelectedStyle] = useState(foundSelectedStyle || {});
	const { nextStep } = useWizard();

	useEffect(() => {
		if (!builderConfig) {
			return;
		}

		const styleIndex = builderConfig.defaultStyleIndex || 0;

		if (builderConfig.styles?.[styleIndex]) {
			setSelectedStyle(builderConfig.styles[styleIndex]);
		}
	}, [builderConfig]);

	const continueWizard = () => {
		// the size step is another component, continue to it.
		if (builderConfig.useSizeStep === true && productConfig.productType.id) {
			nextStep();
			return;
		}

		if (builderConfig.useSizeStep === true) {
			return;
		}

		const nextProductConfigIndex = packConfig.currentProductConfigIndex + 1;
		const productConfigsQuantity = builderConfig.productConfigs?.quantity || 1;

		// you need more product configs to satisfy the builder
		if (nextProductConfigIndex < productConfigsQuantity) {
			updatePackConfig('currentProductConfigIndex', nextProductConfigIndex);
			const nextProductConfig = packConfig.productConfigs[nextProductConfigIndex];
			setProductConfig(nextProductConfig || {});
			window.scrollTo(0, 0);
			return;
		}

		nextStep();
	};

	const updateProductConfig = (key, value) => {
		const toUpdate = { ...productConfig };
		toUpdate[key] = value;
		setProductConfig(toUpdate);

		updatePackProductConfig(packConfig.currentProductConfigIndex, key, value);
		if (key === 'productType') {
			setActiveProductType(value.productType);
		}
	};

	const updateSelectedStyle = (id) => {
		const foundStyle = builderConfig.styles.find((style) => {
			return style.id === id;
		});

		if (foundStyle) {
			setSelectedStyle(foundStyle);
		}
	};

	if (!builderConfig.styles) {
		return null;
	}

	return (
		<div className="flex items-center justify-center align-middle px-4">
			<div className="max-w-xl pt-12 text-center">
				<CutStepTitle builderConfig={builderConfig} packConfig={packConfig} />
				{builderConfig.styles.length > 1 && (
					<div className="flex justify-center gap-2 md:gap-4 mb-2 md:mb-4 pt-4">
						{builderConfig.styles.map((style) => {
							return (
								<Clickable
									className={classNames(
										'h-[44px] font-bold leading-3 max-w-[160px] px-4 rounded-full tracking-widest uppercase w-full',
										selectedStyle.id === style.id
											? 'bg-v2-brown-darker text-white'
											: 'text-v2-brown-darker bg-v2-off-white border-2 border-v2-brown-darker',
									)}
									key={style.id}
									onClick={() => updateSelectedStyle(style.id)}>
									{style.label}
								</Clickable>
							);
						})}
					</div>
				)}

				<div className="grid grid-cols-2 md:flex md:flex-wrap justify-center gap-4 md:gap-8 pt-4 mb-8">
					{selectedStyle?.productTypes?.map((productType, productTypeIndex) => {
						return (
							<Clickable
								key={productTypeIndex}
								onClick={() => updateProductConfig('productType', productType)}
								className="flex flex-col items-center gap-2 md:w-1/5">
								<div
									className={classNames(
										'bg-v2-cream rounded-full h-[120px] w-[120px] flex items-center justify-center mx-auto',
										productType.id === productConfig?.productType?.id
											? 'border-2 border-white ring-2 md:ring-4 ring-v2-brown-darker hover:ring-v2-brown-darker '
											: 'hover:ring-gray-400 hover:border-2 border-white hover:ring-4',
									)}>
									<div className="relative h-[80px] w-[80px] flex items-center justify-center">
										<Image
											src={
												productType.id === productConfig?.productType?.id
													? productType.selectedImg || ''
													: productType.img || ''
											}
											layout="fill"
											objectFit="contain"
											alt={productType.name}
										/>
									</div>
								</div>

								<div className="text-center">
									<Typography className="normal-case" component="h3" variant="subtitle-sm">
										{productType.name}
									</Typography>
									<Typography className="normal-case" component="p" variant="small">
										{productType.details}
									</Typography>
								</div>
							</Clickable>
						);
					})}
				</div>

				{/* Show the 'continue to size step' button if the builder has it on a separate step*/}
				{builderConfig.useSizeStep === true && (
					<div className="sticky bottom-0 w-full bg-v2-off-white p-2 text-center">
						<Clickable
							variant="filled"
							disabled={!productConfig?.productType?.id}
							className="px-4 py-2 font-semibold border-2 border-primary hover:border-primary-dark w-full md:w-auto rounded-md"
							onClick={() => {
								continueWizard();
							}}>
							Continue to Size
						</Clickable>
					</div>
				)}

				{builderConfig.useSizeStep === false && (
					<div>
						<SizeStep
							builderConfig={builderConfig}
							hideContinueButton={true}
							packConfig={packConfig}
							selectedStyleId={selectedStyle?.id}
							updatePackConfig={updatePackConfig}
							updatePackProductConfig={updatePackProductConfig}
						/>
						<div className="sticky bottom-0 w-full bg-v2-off-white p-2 text-center">
							<Clickable
								variant="filled"
								disabled={
									!productConfig?.productType?.id ||
									!packConfig?.productConfigs[packConfig.currentProductConfigIndex]?.size
								}
								className="px-4 py-2 font-semibold border-2 border-primary hover:border-primary-dark w-full md:w-auto rounded-md"
								onClick={() => {
									continueWizard();
								}}>
								Start Packing
							</Clickable>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

CutStep.propTypes = {
	builderConfig: PropTypes.object,
	packConfig: PropTypes.object,
	setActiveProductType: PropTypes.func,
	updatePackConfig: PropTypes.func,
	updatePackProductConfig: PropTypes.func,
};
